import { useState, useEffect, useRef } from 'react'
import { Bell, Loader2 } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/contexts/AuthContext'

const PAGE_SIZE = 20

interface Notification {
    id: string
    created_at: string
    created_by: string | null
    item_id: string
    mentioned_by: string | null
    metadata: any
    notification_type: string
    read_at: string | null
    table_name: string
    worker_id: string
}

interface NotificationBellProps {
    onNavigateToNote?: (noteId: string, beneficiaryId: string) => void
}

export const NotificationBell = ({ onNavigateToNote }: NotificationBellProps) => {
    const { userProfile } = useAuth()
    const [notifications, setNotifications] = useState<Notification[]>([])
    const [open, setOpen] = useState(false)
    const [offset, setOffset] = useState(0)
    const [hasMore, setHasMore] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const buttonRef = useRef<HTMLButtonElement>(null)
    const [dropdownStyle, setDropdownStyle] = useState<{ top: number; left: number }>({ top: 0, left: 0 })

    const unreadCount = notifications.filter(n => n.read_at === null).length

    const fetchPage = async (pageOffset: number, append = false) => {
        if (!userProfile?.worker_id) return
        const { data } = await supabase
            .from('notifications')
            .select('*')
            .eq('worker_id', userProfile.worker_id)
            .order('created_at', { ascending: false })
            .range(pageOffset, pageOffset + PAGE_SIZE - 1)

        if (!data) return
        setHasMore(data.length === PAGE_SIZE)
        setNotifications(prev => append ? [...prev, ...data as Notification[]] : data as Notification[])
    }

    useEffect(() => {
        if (!userProfile?.worker_id) return

        fetchPage(0)

        const channel = supabase
            .channel(`notifications:${userProfile.worker_id}`)
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'notifications',
                    filter: `worker_id=eq.${userProfile.worker_id}`
                },
                (payload) => {
                    setNotifications(prev => [payload.new as Notification, ...prev])
                }
            )
            .subscribe()

        return () => { supabase.removeChannel(channel) }
    }, [userProfile?.worker_id])

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const handleLoadMore = async () => {
        setLoadingMore(true)
        const newOffset = offset + PAGE_SIZE
        await fetchPage(newOffset, true)
        setOffset(newOffset)
        setLoadingMore(false)
    }

    const markOneRead = async (n: Notification) => {
        if (n.read_at !== null) return
        const now = new Date().toISOString()
        await supabase.from('notifications').update({ read_at: now }).eq('id', n.id)
        setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, read_at: now } : x))
    }

    const handleMarkAllRead = async () => {
        if (!userProfile?.worker_id) return
        const now = new Date().toISOString()
        await supabase
            .from('notifications')
            .update({ read_at: now })
            .eq('worker_id', userProfile.worker_id)
            .is('read_at', null)
        setNotifications(prev => prev.map(n => n.read_at === null ? { ...n, read_at: now } : n))
    }

    const handleClickNotification = async (n: Notification) => {
        await markOneRead(n)
        const metadata = typeof n.metadata === 'string' ? JSON.parse(n.metadata) : (n.metadata || {})
        const beneficiaryId = metadata?.beneficiary_id
        if (n.item_id && beneficiaryId && onNavigateToNote) {
            setOpen(false)
            onNavigateToNote(n.item_id, beneficiaryId)
        }
    }

    const getDisplayMessage = (n: Notification) => {
        const metadata = typeof n.metadata === 'string' ? JSON.parse(n.metadata) : (n.metadata || {})
        const sender = metadata.sender_name || 'Un.e collègue'
        const locataire = metadata.locataire || ''
        const msg = metadata.message

        let text = `${sender} vous a mentionné.e`
        if (locataire) text += ` dans une note pour ${locataire}`
        if (msg) text += `. « ${msg} »`
        return text
    }

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
        })
    }

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                ref={buttonRef}
                onClick={() => {
                    if (buttonRef.current) {
                        const rect = buttonRef.current.getBoundingClientRect()
                        const dropdownWidth = Math.min(320, window.innerWidth - 8)
                        setDropdownStyle({
                            top: rect.bottom + 8,
                            left: Math.max(4, rect.right - dropdownWidth)
                        })
                    }
                    setOpen(o => !o)
                }}
                className="relative p-2 rounded-md hover:bg-gray-100 border border-gray-200"
                title="Notifications"
            >
                <Bell size={18} />
                {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 font-medium">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </button>

            {open && (
                <div
                    style={{ position: 'fixed', top: dropdownStyle.top, left: dropdownStyle.left }}
                    className="w-80 max-w-[calc(100vw-1rem)] bg-white rounded-lg shadow-lg border border-gray-200 z-50 flex flex-col"
                >
                    <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100">
                        <span className="font-medium text-sm text-gray-900">Notifications</span>
                        {unreadCount > 0 && (
                            <span className="text-xs text-gray-400">{unreadCount} non lue{unreadCount > 1 ? 's' : ''}</span>
                        )}
                    </div>

                    <div className="max-h-96 overflow-y-auto">
                        {notifications.length === 0 ? (
                            <div className="p-6 text-sm text-gray-500 text-center">
                                Aucune notification
                            </div>
                        ) : (
                            <>
                                {notifications.map(n => (
                                    <div
                                        key={n.id}
                                        className={`px-4 py-3 border-b border-gray-50 transition-colors cursor-pointer
                                            ${n.read_at === null ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-gray-50'}
                                        `}
                                        onClick={() => handleClickNotification(n)}
                                    >
                                        <p className="text-sm text-gray-800 leading-snug">
                                            {getDisplayMessage(n)}
                                        </p>
                                        <p className="text-xs text-gray-400 mt-1">{formatDate(n.created_at)}</p>
                                    </div>
                                ))}

                                {hasMore && (
                                    <button
                                        onClick={handleLoadMore}
                                        disabled={loadingMore}
                                        className="w-full flex items-center justify-center gap-2 py-2 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 transition-colors disabled:opacity-50"
                                    >
                                        {loadingMore
                                            ? <><Loader2 size={12} className="animate-spin" /> Chargement...</>
                                            : 'Charger plus'
                                        }
                                    </button>
                                )}
                            </>
                        )}
                    </div>

                    {notifications.length > 0 && (
                        <div className="border-t border-gray-100 p-2">
                            <button
                                onClick={handleMarkAllRead}
                                className="w-full text-xs text-gray-500 hover:text-gray-700 py-1 hover:bg-gray-50 rounded transition-colors"
                            >
                                Tout marquer comme lu
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
